import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import apiService from 'app/apiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import socketServiceManager from 'app/socketService';
import { EmailTemplatesSocketService } from 'app/socketService/emailTemplates/emailTemplatesSocketService';
import {
  appendNurturingAutomationSocketMessage,
  clearNurturingAutomationState,
  loadNurturingAutomationState,
  saveNurturingAutomationState,
  updateNurturingAutomationProgress,
} from 'app/pages/TestNurturingPage/utils/testNurturingAutomationPersistence';
import {
  getNurturingAutomationJobId,
  getNurturingAutomationLeadCounts,
  getNurturingAutomationSocketMessage,
  NURTURING_AUTOMATION_EVENT_COMPLETED,
  NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS,
  NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS,
  NURTURING_AUTOMATION_EVENT_PREFIX,
  NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED,
} from 'app/utils/nurturingTestAutomationProgressUtils';
import type { NurturingAutomationSocketMessage } from 'app/utils/nurturingTestAutomationProgressUtils';

export type StartNurturingAutomationSessionOptions = {
  jobIds: string[];
  progressTotal?: number;
  /** Used when persisting a single-job session (Test Nurturing). */
  primaryJobId?: string;
};

export type UseNurturingTestAutomationProgressOptions = {
  dealerId: string;
  canAccess: boolean;
  onComplete?: () => void;
  enablePersistence?: boolean;
  invalidatePrefixesOnComplete?: Array<{ prefix: string; method?: string }>;
  progressTitle?: string;
  progressSubtitle?: string;
};

/** Stable default — avoids new array each render when callers omit the option. */
const DEFAULT_INVALIDATE_PREFIXES_ON_COMPLETE: Array<{
  prefix: string;
  method?: string;
}> = [
  { prefix: 'api/v2/leads', method: 'GET' },
  { prefix: 'api/v2/nurturing-automation/opportunities', method: 'GET' },
];

export function useNurturingTestAutomationProgress(
  options: UseNurturingTestAutomationProgressOptions,
) {
  const {
    dealerId,
    canAccess,
    onComplete,
    enablePersistence = false,
    invalidatePrefixesOnComplete:
      invalidatePrefixesOnCompleteOption,
    progressTitle = 'Running nurturing test...',
    progressSubtitle,
  } = options;

  const invalidatePrefixesOnComplete =
    invalidatePrefixesOnCompleteOption ??
    DEFAULT_INVALIDATE_PREFIXES_ON_COMPLETE;

  const dispatch = useDispatch();
  const [automationInProgress, setAutomationInProgress] = useState(false);
  const [automationProgress, setAutomationProgress] = useState({
    current: 0,
    total: 1,
  });
  const [automationSocketMessages, setAutomationSocketMessages] = useState<
    NurturingAutomationSocketMessage[]
  >([]);
  const [automationCompletionPayload, setAutomationCompletionPayload] =
    useState<unknown>(null);

  const automationInProgressRef = useRef(false);
  const activeJobIdsRef = useRef<Set<string>>(new Set());
  const pendingJobIdsRef = useRef<Set<string>>(new Set());
  const progressTotalFallbackRef = useRef(1);
  const onCompleteRef = useRef(onComplete);
  const perJobProgressRef = useRef<Map<string, { current: number; total: number }>>(
    new Map(),
  );
  const dealerIdRef = useRef(dealerId);
  const enablePersistenceRef = useRef(enablePersistence);
  const invalidatePrefixesOnCompleteRef = useRef(invalidatePrefixesOnComplete);
  const aggregateProgressFromJobsRef = useRef<() => {
    current: number;
    total: number;
  }>(() => ({ current: 0, total: 1 }));
  const finalizeSessionRef = useRef<(completionData: unknown) => void>(() => {});

  dealerIdRef.current = dealerId;
  enablePersistenceRef.current = enablePersistence;
  invalidatePrefixesOnCompleteRef.current = invalidatePrefixesOnComplete;

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    automationInProgressRef.current = automationInProgress;
  }, [automationInProgress]);

  useEffect(() => {
    if (!enablePersistence || !dealerId || !canAccess) return;

    const persisted = loadNurturingAutomationState(dealerId);
    if (!persisted?.automationInProgress || !persisted.jobId) {
      return;
    }

    const jobSet = new Set([persisted.jobId]);
    activeJobIdsRef.current = jobSet;
    pendingJobIdsRef.current = new Set(jobSet);
    progressTotalFallbackRef.current = Math.max(1, persisted.progress.total);
    automationInProgressRef.current = true;
    setAutomationInProgress(true);
    setAutomationProgress(persisted.progress);
    setAutomationSocketMessages(persisted.socketMessages ?? []);
    setAutomationCompletionPayload(null);
  }, [dealerId, canAccess, enablePersistence]);

  const aggregateProgressFromJobs = useCallback(() => {
    const entries = Array.from(perJobProgressRef.current.values());
    if (!entries.length) {
      return {
        current: 0,
        total: Math.max(1, progressTotalFallbackRef.current),
      };
    }
    const total = entries.reduce((sum, e) => sum + Math.max(1, e.total), 0);
    const current = entries.reduce((sum, e) => sum + Math.max(0, e.current), 0);
    return {
      current: Math.min(current, total),
      total: Math.max(1, total),
    };
  }, []);

  const finalizeSession = useCallback(
    (completionData: unknown) => {
      const activeDealerId = dealerIdRef.current;
      automationInProgressRef.current = false;
      activeJobIdsRef.current = new Set();
      pendingJobIdsRef.current = new Set();
      perJobProgressRef.current = new Map();
      setAutomationInProgress(false);
      setAutomationCompletionPayload(completionData);

      if (activeDealerId && enablePersistenceRef.current) {
        clearNurturingAutomationState(activeDealerId);
      }

      const completionMessageRaw =
        getNurturingAutomationSocketMessage(completionData).message;
      const completionMessage =
        typeof completionMessageRaw === 'string'
          ? completionMessageRaw.trim()
          : '';
      if (completionMessage) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message: completionMessage,
          }),
        );
      }

      for (const item of invalidatePrefixesOnCompleteRef.current) {
        void apiService.invalidateCacheByPrefix(
          item.prefix,
          item.method ?? 'GET',
        );
      }

      onCompleteRef.current?.();
    },
    [dispatch],
  );

  aggregateProgressFromJobsRef.current = aggregateProgressFromJobs;
  finalizeSessionRef.current = finalizeSession;

  const startSession = useCallback(
    (session: StartNurturingAutomationSessionOptions) => {
      const jobIds = session.jobIds.map(id => String(id).trim()).filter(Boolean);
      const jobSet = new Set(jobIds);
      const primaryJobId = session.primaryJobId?.trim() || jobIds[0] || '';
      const total = Math.max(1, session.progressTotal ?? 1);

      progressTotalFallbackRef.current = total;
      activeJobIdsRef.current = jobSet;
      pendingJobIdsRef.current = new Set(jobSet);
      perJobProgressRef.current = new Map();

      setAutomationCompletionPayload(null);
      setAutomationSocketMessages([]);
      automationInProgressRef.current = true;
      setAutomationInProgress(true);
      setAutomationProgress({ current: 0, total });

      if (enablePersistence && dealerId && primaryJobId) {
        saveNurturingAutomationState(dealerId, {
          automationInProgress: true,
          jobId: primaryJobId,
          progress: { current: 0, total },
          socketMessages: [],
        });
      }
    },
    [dealerId, enablePersistence],
  );

  const resetSession = useCallback(() => {
    automationInProgressRef.current = false;
    activeJobIdsRef.current = new Set();
    pendingJobIdsRef.current = new Set();
    perJobProgressRef.current = new Map();
    setAutomationInProgress(false);
    setAutomationProgress({ current: 0, total: 1 });
    setAutomationSocketMessages([]);
    setAutomationCompletionPayload(null);
    if (dealerId && enablePersistence) {
      clearNurturingAutomationState(dealerId);
    }
  }, [dealerId, enablePersistence]);

  useEffect(() => {
    if (!dealerId || !canAccess) return undefined;

    const socketUrl =
      process.env.REACT_APP_WS_URL ||
      process.env.REACT_APP_API_URL ||
      'http://localhost:3000';
    const socketConfig = {
      url: socketUrl,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };

    socketServiceManager.initializeGlobalSocket(socketConfig);
    const service = EmailTemplatesSocketService.getInstance(socketConfig);
    socketServiceManager.registerService(service);
    service.connect();

    const callback = (eventName: string, data: unknown) => {
      if (!eventName.startsWith(NURTURING_AUTOMATION_EVENT_PREFIX)) return;

      const isCompletionEvent = eventName === NURTURING_AUTOMATION_EVENT_COMPLETED;
      const sessionHasActiveAutomation =
        automationInProgressRef.current ||
        activeJobIdsRef.current.size > 0 ||
        pendingJobIdsRef.current.size > 0;
      if (!sessionHasActiveAutomation) return;

      if (!isCompletionEvent && !automationInProgressRef.current) return;

      const eventJobId = getNurturingAutomationJobId(data);
      const trackedJobIds = activeJobIdsRef.current;
      const pendingJobIds = pendingJobIdsRef.current;

      if (eventJobId && trackedJobIds.size > 0 && !trackedJobIds.has(eventJobId)) {
        return;
      }

      const socketMessage: NurturingAutomationSocketMessage = {
        eventName,
        data,
        timestamp: Date.now(),
      };
      setAutomationSocketMessages(prev => [...prev, socketMessage].slice(-100));
      const activeDealerId = dealerIdRef.current;
      if (activeDealerId && enablePersistenceRef.current) {
        appendNurturingAutomationSocketMessage(activeDealerId, socketMessage);
      }

      const { current, total } = getNurturingAutomationLeadCounts(data);
      if (
        eventName === NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED ||
        eventName === NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS ||
        eventName === NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS
      ) {
        if (eventJobId) {
          const prevJob = perJobProgressRef.current.get(eventJobId) ?? {
            current: 0,
            total: progressTotalFallbackRef.current,
          };
          const totalResolved =
            total != null
              ? Math.max(1, total)
              : prevJob.total > 0
                ? prevJob.total
                : progressTotalFallbackRef.current;
          const currentResolved =
            current != null
              ? Math.min(Math.max(0, current), totalResolved)
              : prevJob.current;
          perJobProgressRef.current.set(eventJobId, {
            current: currentResolved,
            total: totalResolved,
          });
        }

        setAutomationProgress(() => {
          const next = eventJobId
            ? aggregateProgressFromJobsRef.current()
            : {
                current:
                  current != null
                    ? Math.min(
                        Math.max(0, current),
                        Math.max(1, progressTotalFallbackRef.current),
                      )
                    : 0,
                total: Math.max(
                  1,
                  total ?? progressTotalFallbackRef.current,
                ),
              };
          if (activeDealerId && enablePersistenceRef.current) {
            updateNurturingAutomationProgress(activeDealerId, next);
          }
          return next;
        });
      }

      if (eventName === NURTURING_AUTOMATION_EVENT_COMPLETED) {
        if (eventJobId) {
          pendingJobIds.delete(eventJobId);
          const totalResolved =
            total != null
              ? Math.max(1, total)
              : progressTotalFallbackRef.current;
          const currentResolved =
            current != null
              ? Math.min(Math.max(0, current), totalResolved)
              : totalResolved;
          perJobProgressRef.current.set(eventJobId, {
            current: currentResolved,
            total: totalResolved,
          });
        }

        setAutomationProgress(prev => {
          const aggregated = eventJobId
            ? aggregateProgressFromJobsRef.current()
            : {
                current:
                  current != null
                    ? Math.min(
                        Math.max(0, current),
                        Math.max(1, prev.total),
                      )
                    : prev.total,
                total: Math.max(1, total ?? prev.total),
              };
          return aggregated;
        });

        const allJobsDone =
          pendingJobIds.size === 0 || trackedJobIds.size === 0;
        if (allJobsDone) {
          finalizeSessionRef.current(data);
        }
      }
    };

    service.subscribeToDealer(dealerId, callback);

    return () => {
      service.unsubscribeFromDealer(dealerId, callback);
    };
  }, [canAccess, dealerId]);

  const automationProgressPercentage = useMemo(() => {
    const total = Math.max(automationProgress.total, 1);
    return Math.min(
      100,
      Math.max(0, Math.round((automationProgress.current / total) * 100)),
    );
  }, [automationProgress.current, automationProgress.total]);

  const automationStreamingDataForCard = useMemo(() => {
    const totalSections = Math.max(automationProgress.total, 1);
    return {
      message: {
        sections: Array.from({ length: totalSections }, (_, i) => ({
          index: i + 1,
          main: `Lead ${i + 1}`,
          sub_text: '',
        })),
      },
    };
  }, [automationProgress.total]);

  const automationCurrentSectionForCard = useMemo(
    () => ({
      index: Math.min(
        Math.max(1, automationProgress.current || 1),
        Math.max(automationProgress.total, 1),
      ),
      type: 'step' as const,
      main: progressTitle,
      sub_text:
        progressSubtitle ??
        `${automationProgress.current}/${Math.max(
          automationProgress.total,
          1,
        )} leads processed`,
    }),
    [
      automationProgress.current,
      automationProgress.total,
      progressSubtitle,
      progressTitle,
    ],
  );

  return {
    automationInProgress,
    automationProgressPercentage,
    automationProgressCurrent: automationProgress.current,
    automationProgressTotal: automationProgress.total,
    automationStreamingDataForCard,
    automationCurrentSectionForCard,
    automationSocketMessages,
    automationCompletionPayload,
    startSession,
    resetSession,
  };
}
